// src/components/Footer.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationPin, faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { GraduationCap, BookOpen, Users, ChevronRight } from 'lucide-react';
import '../styles/Footer.scss';

const socialLinks = [
  {
    href: 'https://www.facebook.com/profile.php?id=61562571579177',
    label: 'Facebook',
    icon: '/icons/social-media/facebook.png',
    alt: 'Facebook'
  },
  {
    href: 'https://www.instagram.com/prabhattaravidyaniketan/',
    label: 'Instagram',
    icon: '/icons/social-media/instagram.png',
    alt: 'Instagram'
  },
  {
    href: 'https://www.youtube.com/@ptvn_goraul',
    label: 'YouTube',
    icon: '/icons/social-media/youtube.png',
    alt: 'YouTube'
  },
  {
    href: 'https://www.google.com/search?q=prabhat+tara+vidya+niketan',
    label: 'Google Reviews',
    icon: '/icons/social-media/review-us.png',
    alt: 'Review us'
  }
];

const FooterWave = () => (
  <div className="footer-wave" aria-hidden="true">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 80" preserveAspectRatio="none">
      <path fill="#011673" d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z" />
    </svg>
  </div>
);

const Footer = () => {
  const { t, i18n } = useTranslation();
  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('languageSet', 'true');
  };

  const quickLinks = [
    { href: '/', label: t('home') },
    { href: '/about-us', label: t('about_us') },
    { href: '/academics', label: t('academics') },
    { href: '/the-school', label: t('the_school') },
    { href: '/mandatory-disclosure', label: t('mandatory_disclosure') },
    { href: '/teaching-staff', label: t('teaching_staff') },
    { href: '/fee-structure', label: t('fee_structure') },
    { href: '/gallery', label: t('gallery') },
    { href: '/contact-us', label: t('contact_us') },
  ];

  const admissionLinks = [
    { href: '/admission', label: t('admission') },
    { href: '/admission#required-documents', label: t('nav_required_documents') },
    { href: '/admission#age-criteria', label: t('nav_age_criteria') },
    { href: '/fee-structure#payment-guidelines', label: t('nav_payment_guidelines') },
    { href: '/uniform-schedule', label: t('uniform_schedule') },
    { href: '/exam-corner', label: 'Exam Corner' },
    { href: '/job-enquiry', label: 'Career / Job Enquiry' },
  ];

  return (
    <footer className="school-footer">
      <FooterWave />
      <div className="footer-body">
        <div className="footer-col footer-brand">
          <div className="brand-logo-wrap">
            <img src="/images/logo.jpg" alt={`${t('school_name')} Logo`} loading="lazy" />
            <div className="brand-tag"><GraduationCap size={14} /> Since 1995</div>
          </div>
          <h2>{t('school_name')}</h2>
          <p className="brand-tagline">Shaping futures through excellence — Nursery to Class 10</p>
          <p className="brand-address"><FontAwesomeIcon icon={faLocationPin} /> {t('school_address')}</p>
          <p className="brand-affil"><BookOpen size={14} /> {t('school_tagline_bar')}</p>
          <div className="footer-social">
            {socialLinks.map(({ href, label, icon, alt }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                <img src={icon} alt={alt} loading="lazy" />
              </a>
            ))}
          </div>
        </div>

        <div className="footer-col">
          <h3>{t('quick_links')}</h3>
          <ul>
            {quickLinks.map(({ href, label }) => (
              <li key={href}><a href={href}><ChevronRight size={14} /> {label}</a></li>
            ))}
          </ul>
        </div>

        <div className="footer-col">
          <h3>{t('admission')}</h3>
          <ul>
            {admissionLinks.map(({ href, label }) => (
              <li key={href}><a href={href}><ChevronRight size={14} /> {label}</a></li>
            ))}
          </ul>
          <h3 style={{ marginTop: '2rem' }}>{t('contact_us')}</h3>
          <ul className="contact-list">
            <li><FontAwesomeIcon icon={faPhone} /> <a href="tel:7654164726">+91 76541 64726</a></li>
            <li><FontAwesomeIcon icon={faPhone} /> <a href="tel:9939988606">+91 99399 88606</a></li>
            <li><FontAwesomeIcon icon={faEnvelope} /> <a href="mailto:principal@prabhatvidyanikethan.com">principal@prabhatvidyanikethan.com</a></li>
          </ul>
        </div>

        <div className="footer-col footer-stats-col">
          <h3>Our Numbers</h3>
          <div className="footer-stats">
            <div className="fs-item"><GraduationCap size={22} /><div><span className="fs-val">1200+</span><span className="fs-lab">{t('stats_students')}</span></div></div>
            <div className="fs-item"><Users size={22} /><div><span className="fs-val">50+</span><span className="fs-lab">{t('stats_educators')}</span></div></div>
            <div className="fs-item"><BookOpen size={22} /><div><span className="fs-val">30+</span><span className="fs-lab">{t('stats_years')}</span></div></div>
          </div>
          <h3 style={{ marginTop: '2rem' }}>Compliance & Legal</h3>
          <ul>
            <li><a href="/mandatory-disclosure"><ChevronRight size={14} /> {t('mandatory_disclosure')}</a></li>
            <li><a href="/privacy-policy"><ChevronRight size={14} /> {t('privacy_policy')}</a></li>
            <li><a href="/terms-of-service"><ChevronRight size={14} /> {t('terms_of_service')}</a></li>
          </ul>
        </div>
      </div>

      <div className="footer-bottom">
        <p>
          &copy; {new Date().getFullYear()} {t('school_name')} Goraul. {t('footer_rights')} • Exam Evaluation & Assessment Powered by{' '}
          <a href="https://scanscore.app" target="_blank" rel="noopener noreferrer" style={{ color: '#ffd875', fontWeight: 600, textDecoration: 'underline' }}>
            ScanScore (scanscore.app)
          </a>
        </p>
        <div className="lang-switcher">
          <button onClick={() => handleLanguageChange('hi')} aria-label="Switch to Hindi"><img src="/icons/ind.png" alt="" /> हिंदी</button>
          <button onClick={() => handleLanguageChange('en')} aria-label="Switch to English"><img src="/icons/eng.png" alt="" /> English</button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
